import React from 'react';

import { Box, Paper, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { useGLMCardStyles } from '../styles';
import { FormValues, KiloGLMFlowActions, KiloGLMFlowTriggers, TRIGGER_LABELS } from '../types';

const TRIGGER_DESCRIPTIONS: Record<KiloGLMFlowTriggers, string> = {
  [KiloGLMFlowTriggers.ATHLETE_CREATED]:
    'Fires when a new athlete is created in Kilo. Does not fire for dependants or linked athletes.',
  [KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED]:
    "Data sync (continuous): when an athlete's data changes, fills empty fields on the GLM contact. Kilo wins; GLM is only filled where empty. Email is never synced after creation.",
  [KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED]:
    "Fires when any change is made to an athlete's current status in Kilo.",
  [KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED]:
    'Fires when an athlete reaches a defined total number of check-ins (class or appointment).',
  [KiloGLMFlowTriggers.CLASS_CHECKIN]: 'Fires each time an athlete checks into a class.',
  [KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN]: 'Fires each time an athlete checks into an appointment.',
  [KiloGLMFlowTriggers.PACKAGE_STARTED]: "Fires on the start date of an athlete's package.",
  [KiloGLMFlowTriggers.PACKAGE_PURCHASED]:
    'Triggers when an athlete purchases a package in Kilo, before the package start date.',
  [KiloGLMFlowTriggers.PACKAGE_ENDED]: "Fires when an athlete's package reaches its end date.",
  [KiloGLMFlowTriggers.PACKAGE_CANCELLED]: 'Fires when a package is cancelled before its planned end date.',
  [KiloGLMFlowTriggers.PACKAGE_HOLD_START]: "Fires when an athlete's package is placed on hold.",
  [KiloGLMFlowTriggers.PACKAGE_HOLD_END]: "Fires when an athlete's package hold is removed or ends.",
  [KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED]:
    'Triggers when a note is created or edited for an athlete in Kilo.',
  [KiloGLMFlowTriggers.NOTE_DELETED]: 'Triggers when a note is deleted for an athlete in Kilo.',
};

const TRIGGER_GROUPS: { label: string; triggers: KiloGLMFlowTriggers[] }[] = [
  {
    label: 'Lifecycle',
    triggers: [KiloGLMFlowTriggers.ATHLETE_CREATED],
  },
  {
    label: 'Data Sync',
    triggers: [KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED],
  },
  {
    label: 'Athlete Events',
    triggers: [
      KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED,
      KiloGLMFlowTriggers.CLASS_CHECKIN,
      KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN,
      KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED,
    ],
  },
  {
    label: 'Package Events',
    triggers: [
      KiloGLMFlowTriggers.PACKAGE_STARTED,
      KiloGLMFlowTriggers.PACKAGE_PURCHASED,
      KiloGLMFlowTriggers.PACKAGE_ENDED,
      KiloGLMFlowTriggers.PACKAGE_CANCELLED,
      KiloGLMFlowTriggers.PACKAGE_HOLD_START,
      KiloGLMFlowTriggers.PACKAGE_HOLD_END,
    ],
  },
  {
    label: 'Notes',
    triggers: [KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED, KiloGLMFlowTriggers.NOTE_DELETED],
  },
];

interface IKiloGLMTriggerSelectProps {
  handleTriggerSelect: (value: string) => void;
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
}

const KiloGLMTriggerSelect = ({ handleTriggerSelect, form }: IKiloGLMTriggerSelectProps): JSX.Element => {
  const { classes, cx } = useGLMCardStyles();

  const handleSelectTrigger = (value: KiloGLMFlowTriggers): void => {
    handleTriggerSelect(value);
    // Auto-set the action for triggers that have exactly one valid action.
    // ATHLETE_CREATED is NOT auto-set — the user chooses between the two link
    // composite actions on the action step.
    if (value === KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED) {
      form.setFieldValue('action', KiloGLMFlowActions.UPSERT_NOTE as FormValues['action']);
    } else if (value === KiloGLMFlowTriggers.NOTE_DELETED) {
      form.setFieldValue('action', KiloGLMFlowActions.DELETE_NOTE as FormValues['action']);
    } else if (value === KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED) {
      form.setFieldValue('action', KiloGLMFlowActions.SYNC_CONTACT_DATA as FormValues['action']);
    }
  };

  return (
    <Paper>
      <Stack sx={{ paddingTop: '20px' }}>
        <Title order={3}>What should kick this off?</Title>
        <Text size={15}>Pick what happens in Kilo that should trigger an action in GLM.</Text>
        <Stack spacing='md' style={{ width: '100%' }}>
          {TRIGGER_GROUPS.map(group => (
            <Stack key={group.label} spacing={6}>
              <Text size='xs' weight={600} color='dimmed' className={classes.groupLabel}>
                {group.label}
              </Text>
              <Stack spacing={6}>
                {group.triggers.map(trigger => {
                  const isSelected = form.values.trigger === trigger;
                  return (
                    <UnstyledButton key={trigger} onClick={() => handleSelectTrigger(trigger)}>
                      <Box className={cx(classes.card, { [classes.cardSelected]: isSelected })}>
                        <Text weight={isSelected ? 600 : 400}>{TRIGGER_LABELS[trigger]}</Text>
                        {isSelected && (
                          <Text size='sm' color='#6B6B6B' mt={4}>
                            {TRIGGER_DESCRIPTIONS[trigger]}
                          </Text>
                        )}
                      </Box>
                    </UnstyledButton>
                  );
                })}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default KiloGLMTriggerSelect;
